import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { Submission, Client } from '../types.js';
import { getSubmission, getClient, deleteSubmission, resendEmail, retriggerWebhook } from '../api.js';

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function SubmissionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');

  useEffect(() => {
    if (!id) return;
    getSubmission(id)
      .then((res) => {
        setSubmission(res.submission);
        return getClient(res.submission.client_id);
      })
      .then((res) => setClient(res.client))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [id]);

  async function handleDelete() {
    if (!submission) return;
    if (!confirm('Delete this submission?')) return;
    try {
      await deleteSubmission(submission.id);
      navigate(`/clients/${submission.client_id}/submissions`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  async function handleResendEmail() {
    if (!submission) return;
    try {
      await resendEmail(submission.id);
      setActionMsg('Email resend queued.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Resend failed');
    }
  }

  async function handleRetriggerWebhook() {
    if (!submission) return;
    try {
      await retriggerWebhook(submission.id);
      setActionMsg('Webhook retrigger queued.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Retrigger failed');
    }
  }

  function badge(status: string) {
    if (status === 'sent')    return <span className="badge badge-green">Sent</span>;
    if (status === 'failed')  return <span className="badge badge-red">Failed</span>;
    if (status === 'pending') return <span className="badge badge-yellow">Pending</span>;
    return <span className="badge badge-grey">—</span>;
  }

  if (error) return <div className="alert alert-error">{error}</div>;
  if (!submission) return <p style={{ color: '#94a3b8' }}>Loading…</p>;

  const fields = client?.form_schema.fields ?? [];

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Submission</h1>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <Link to={`/clients/${submission.client_id}/submissions`} className="btn btn-secondary">← Back</Link>
        </div>
      </div>

      {actionMsg && <div className="alert alert-success">{actionMsg}</div>}

      {/* Captured while test mode was armed. Spelled out because a GENUINE lead can
          land in that window: delivery was suppressed, so the grey status badges
          below are expected and Resend/Retrigger is the recovery path. */}
      {submission.is_test && (
        <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
          <strong>🧪 Test submission</strong> — captured while test mode was active, so no email was
          sent to the client and no webhook was dispatched. If this turns out to be a real enquiry,
          use <strong>Resend email</strong> / <strong>Retrigger webhook</strong> below to deliver it.
        </div>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '.75rem' }}>Submission data</h2>
        <table>
          <tbody>
            {fields.length > 0
              ? fields.map((f) => (
                  <tr key={f.name}>
                    <th style={{ width: '180px' }}>{f.label}</th>
                    <td>{String(submission.form_data[f.name] ?? '—')}</td>
                  </tr>
                ))
              : Object.entries(submission.form_data).map(([k, v]) => (
                  <tr key={k}>
                    <th style={{ width: '180px' }}>{k}</th>
                    <td>{String(v)}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {submission.files && submission.files.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '.75rem' }}>Files</h2>
          <table style={{ tableLayout: 'fixed' }}>
            <tbody>
              {submission.files.map((f) => (
                <tr key={f.id}>
                  <th style={{ width: '180px' }}>{fields.find((x) => x.name === f.field)?.label ?? f.field}</th>
                  <td style={{ overflowWrap: 'anywhere' }}>
                    {f.url ? (
                      <a href={f.url} target="_blank" rel="noopener noreferrer">{f.filename}</a>
                    ) : (
                      <>
                        {f.filename} <span style={{ color: '#94a3b8' }}>(download unavailable)</span>
                      </>
                    )}
                    <span style={{ color: '#64748b', marginLeft: '.5rem' }}>{formatBytes(f.size)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="form-hint" style={{ marginTop: '.5rem' }}>
            Download links expire after an hour. Reload this page for fresh ones.
          </div>
        </div>
      )}

      {submission.source_meta && Object.keys(submission.source_meta).length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '.75rem' }}>Source / Attribution</h2>
          <table>
            <tbody>
              {Object.entries(submission.source_meta).map(([k, v]) => (
                <tr key={k}>
                  <th style={{ width: '180px' }}>{k}</th>
                  <td>{String(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '.75rem' }}>Metadata</h2>
        <table>
          <tbody>
            <tr><th style={{ width: '180px' }}>ID</th><td><code>{submission.id}</code></td></tr>
            <tr><th>Submitted</th><td>{new Date(submission.created_at * 1000).toLocaleString('en-AU')}</td></tr>
            <tr><th>IP</th><td>{submission.ip ?? '—'}</td></tr>
            <tr><th>User agent</th><td style={{ wordBreak: 'break-all', fontSize: '.8rem' }}>{submission.user_agent ?? '—'}</td></tr>
            <tr><th>Expires</th><td>{new Date(submission.expires_at * 1000).toLocaleDateString('en-AU')}</td></tr>
          </tbody>
        </table>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '.75rem' }}>Action statuses</h2>
        <table>
          <tbody>
            <tr>
              <th style={{ width: '180px' }}>Email</th>
              <td>{badge(submission.email_status)} &nbsp; {submission.email_attempts} attempt{submission.email_attempts !== 1 ? 's' : ''}</td>
            </tr>
            {submission.email_last_error && (
              <tr><th>Email error</th><td style={{ color: '#dc2626', fontSize: '.8rem' }}>{submission.email_last_error}</td></tr>
            )}
            <tr>
              <th>Webhook</th>
              <td>{badge(submission.webhook_status)} &nbsp; {submission.webhook_attempts} attempt{submission.webhook_attempts !== 1 ? 's' : ''}</td>
            </tr>
            {submission.webhook_last_error && (
              <tr><th>Webhook error</th><td style={{ color: '#dc2626', fontSize: '.8rem' }}>{submission.webhook_last_error}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: '.75rem', flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" onClick={() => { void handleResendEmail(); }}>Resend email</button>
        <button className="btn btn-secondary" onClick={() => { void handleRetriggerWebhook(); }}>Retrigger webhook</button>
        <button className="btn btn-danger" onClick={() => { void handleDelete(); }}>Delete submission</button>
      </div>
    </>
  );
}
